// Filepath: // src/App.js
import React, { useEffect, useState } from "react";
import { ThemeProvider, CssBaseline, Box } from "@mui/material";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import theme from "./theme";
import { SceneProvider, useScene } from "./state/SceneContext";
import { UserProvider } from "./state/UserContext";

import GreetingScene from "./components/scenes/GreetingScene";
import HomeScene from "./components/scenes/HomeScene";
import ItemDetailScene from "./components/scenes/ItemDetailScene";
import VideoPlayerScene from "./components/scenes/VideoPlayerScene";
import SettingsScene from "./components/scenes/SettingsScene";
import GalleryScene from "./components/scenes/GalleryScene";
import IntroPairingScreen from "./components/scenes/IntroPairingScene";
import SignInPage from "./pages/auth/SignInPage";
import SignUpPage from "./pages/auth/SignUpPage";
import InteractPage from "./pages/InteractPage";
import GridScene from "./components/scenes/GridScene";
import SearchScene from "./components/scenes/SearchScene";
import ProfileScene from "./components/scenes/ProfileScene";
import { installRemote } from "./input/remote";
import ExitPrompt from "./components/system/ExitPrompt";
import PairPage from "./pages/PairPage";

// Route-aware viewport controller
import useAdaptiveViewport from "./hooks/useAdaptiveViewport";

function SceneSwitch() {
  const { scene } = useScene();
  switch (scene.name) {
    case "intro": return <IntroPairingScreen />;
    case "greeting": return <GreetingScene />;
    case "home": return <HomeScene />;
    case "grid": return <GridScene {...(scene.params || {})} />;
    case "item": return <ItemDetailScene />;
    case "player": return <VideoPlayerScene />;
    case "gallery": return <GalleryScene />;
    case "settings": return <SettingsScene />;
    case "search": return <SearchScene />;
    case "profile": return <ProfileScene {...(scene.params || {})} />;
    default: return <HomeScene />;
  }
}

// Isolated TV shell so we don't install DPAD on /pair
function TVShell() {
  const [showExit, setShowExit] = useState(false);

  useEffect(() => {
    const off = installRemote();
    return off;
  }, []);

  useEffect(() => {
    const openExit = () => setShowExit(true);
    window.addEventListener("request-exit-app", openExit);
    return () => window.removeEventListener("request-exit-app", openExit);
  }, []);

  return (
    <UserProvider>
      <SceneProvider>
        <Box
          id="app-root"
          sx={{
            width: "100%",
            height: "100%",
            bgcolor: "background.default",
            color: "text.primary",
            position: "relative",
          }}
        >
          <SceneSwitch />
          <ExitPrompt open={showExit} onClose={() => setShowExit(false)} />
        </Box>
      </SceneProvider>
    </UserProvider>
  );
}

// Helper component so the viewport hook runs inside Router
function ViewportController() {
  useAdaptiveViewport();
  return null;
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <ViewportController />
        <Routes>
          {/* 2nd-screen pairing route: https://<domain>/pair?code=JGJ123 */}
          <Route path="/pair" element={<PairPage />} />
          <Route path="/interact" element={<InteractPage />} />
          <Route path="/signin" element={<SignInPage />} />
          <Route path="/signup" element={<SignUpPage />} />
          {/* All other routes render the TV app shell */}
          <Route path="/*" element={<TVShell />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}
